import _ from "lodash";
import { Trans } from "react-i18next";
import i18next from "src/i18n/config";

import { DeviceEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";

import { createDeviceLink, createGroupLink, extractPlainText, getActorTitle } from "./shared/eventHelpers";
import type { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type DeviceCreatedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Created" }>;
const renderDeviceCreated = (props: RendererProps<DeviceCreatedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    const deviceLabel = extractPlainText(device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceCreatedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceCreatedDescription"
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceUpdatedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Updated" }>;
const renderDeviceUpdated = (props: RendererProps<DeviceUpdatedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    if (!event.data) {
        return {
            color: color[event.level],
            header: i18next.t("modals:eventModals.deviceUpdatedTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.deviceUpdatedDescriptionWithoutData"
                    values={{
                        actorTitle: actorLabel,
                        entityId: event.entityId,
                    }}
                />
            ),
        };
    }

    const deviceName = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupName = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");
    const oldVersion = event.data.emteriaVersion.old;
    const newVersion = event.data.emteriaVersion.new;
    const oldVersionMessage = oldVersion
        ? "eventModals.deviceUpdatedDescription"
        : "eventModals.deviceUpdatedDescriptionWithoutOldVersion";

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceUpdatedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey={oldVersionMessage}
                values={{
                    deviceName,
                    groupName,
                    oldVersion,
                    newVersion,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceActivatedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Activated" }>;
const renderDeviceActivated = (props: RendererProps<DeviceActivatedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const deviceName = device?.name ?? event.entityId ?? i18next.t("common:status.unknown");
    const groupName = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceActivatedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceActivatedDescription"
                values={{
                    deviceName,
                    groupName,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceLocationChangedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.LocationChanged" }>;
const renderDeviceLocationChanged = (props: RendererProps<DeviceLocationChangedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const deviceLabel = extractPlainText(device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceLocationChangedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceLocationChangedDescription"
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceBatteryAlertDetectedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.BatteryAlertDetected" }>;
const renderDeviceBatteryAlertDetected = (props: RendererProps<DeviceBatteryAlertDetectedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const deviceLabel = extractPlainText(device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));
    const percentage = event.data?.battery?.new ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceBatteryAlertDetectedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceBatteryAlertDetectedDescription"
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                    percentage,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

const formatBatteryHealth = (value?: string | null): string => {
    switch (value) {
        case "GOOD":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.good");
        case "OVERHEAT":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.overheat");
        case "DEAD":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.dead");
        case "OVER_VOLTAGE":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.overVoltage");
        case "UNSPECIFIED_FAILURE":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.failure");
        case "COLD":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.cold");
        case "FAIR":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.fair");
        case "NOT_AVAILABLE":
            return i18next.t("sidepanels:devicePanel.hardwareTab.notAvailable");
        case "INCONSISTENT":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.inconsistent");
        default:
            return i18next.t("common:status.unknown");
    }
};

const formatBatteryStatus = (value?: string): string => {
    switch (value) {
        case "CHARGING":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.charging");
        case "DISCHARGING":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.discharging");
        case "NOT_CHARGING":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.notCharging");
        case "FULL":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.full");
        default:
            return i18next.t("common:status.unknown");
    }
};

const formatBatteryPlugged = (value?: string): string => {
    switch (value) {
        case "BATTERY_PLUGGED_AC":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.ac");
        case "BATTERY_PLUGGED_USB":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.usb");
        case "BATTERY_PLUGGED_WIRELESS":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.wireless");
        case "BATTERY_PLUGGED_DOCK":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.dock");
        case "NONE":
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.none");
        default:
            return i18next.t("common:status.unknown");
    }
};

const formatVoltage = (voltageMv?: number): string =>
    typeof voltageMv === "number" ? `${(voltageMv / 1000).toFixed(2)}V` : i18next.t("common:status.unknown");

const formatTemperature = (temperatureTenthsC?: number): string =>
    typeof temperatureTenthsC === "number" ? `${(temperatureTenthsC / 10).toFixed(1)}°C` : i18next.t("common:status.unknown");

const formatPercentage = (percentage?: number): string =>
    typeof percentage === "number" ? `${percentage}%` : i18next.t("common:status.unknown");

type DeviceBatteryHealthDegradedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.BatteryHealthDegraded" }>;
const renderDeviceBatteryHealthDegraded = (props: RendererProps<DeviceBatteryHealthDegradedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    if (!event.data) {
        return {
            color: color[event.level],
            header: i18next.t("modals:eventModals.deviceBatteryHealthDegradedTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.deviceBatteryHealthDegradedDescriptionWithoutData"
                    values={{ entityId: event.entityId }}
                />
            ),
        };
    }

    const deviceLabel = extractPlainText(device?.name ?? event.data.deviceSerial ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));
    const snapshot = event.data.battery_snapshot;
    const hasOldHealth = !!event.data.health.old;

    if (_.isEmpty(snapshot)) {
        return {
            color: color[event.level],
            header: i18next.t("modals:eventModals.deviceBatteryHealthDegradedTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey={
                        hasOldHealth
                            ? "eventModals.deviceBatteryHealthDegradedDescriptionWithoutSnapshot"
                            : "eventModals.deviceBatteryHealthDegradedDescriptionWithoutSnapshotAndOldHealth"
                    }
                    values={{
                        deviceName: deviceLabel,
                        groupName: groupLabel,
                        oldHealth: formatBatteryHealth(event.data.health.old),
                        newHealth: formatBatteryHealth(event.data.health.new),
                    }}
                    components={{
                        groupLink: createGroupLink(event, context),
                        deviceLink: createDeviceLink(event, context),
                    }}
                />
            ),
        };
    }

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceBatteryHealthDegradedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey={
                    hasOldHealth
                        ? "eventModals.deviceBatteryHealthDegradedDescription"
                        : "eventModals.deviceBatteryHealthDegradedDescriptionWithoutOldHealth"
                }
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                    oldHealth: formatBatteryHealth(event.data.health.old),
                    newHealth: formatBatteryHealth(event.data.health.new),
                    status: formatBatteryStatus(snapshot.status),
                    plugged: formatBatteryPlugged(snapshot.plugged),
                    voltage: formatVoltage(snapshot.voltage),
                    percentage: formatPercentage(snapshot.percentage),
                    temperature: formatTemperature(snapshot.temperature),
                    groupLink: createGroupLink(event, context),
                    deviceLink: createDeviceLink(event, context),
                }}
                components={{
                    groupLink: createGroupLink(event, context),
                    deviceLink: createDeviceLink(event, context),
                }}
            />
        ),
    };
};
type DeviceMemoryAlertDetectedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.MemoryAlertDetected" }>;
const renderDeviceMemoryAlertDetected = (props: RendererProps<DeviceMemoryAlertDetectedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const deviceLabel = extractPlainText(device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceMemoryAlertDetectedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceMemoryAlertDetectedDescription"
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceRenamedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Renamed" }>;
const renderDeviceRenamed = (props: RendererProps<DeviceRenamedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    const deviceLabel = extractPlainText(device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceRenamedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceRenamedDescription"
                values={{
                    newName: deviceLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceDeletedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Deleted" }>;
const renderDeviceDeleted = (props: RendererProps<DeviceDeletedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { group } = context;
    const color = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    const deviceLabel = extractPlainText(event.data?.deviceSerial ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceDeletedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceDeletedDescription"
                values={{
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                    deviceSerial: deviceLabel,
                }}
                components={{
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceBatteryLevelChangedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.BatteryLevelChanged" }>;
const renderDeviceBatteryLevelChanged = (props: RendererProps<DeviceBatteryLevelChangedEvent>): EventContentResult | null => {
    const { event, theme } = props;

    const color = getEventColors(theme);

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceBatteryLevelChangedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceBatteryLevelChangedDescription"
                values={{
                    entityId: event.entityId,
                }}
            />
        ),
    };
};

type DeviceMemoryLevelChangedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.MemoryLevelChanged" }>;
const renderDeviceMemoryLevelChanged = (props: RendererProps<DeviceMemoryLevelChangedEvent>): EventContentResult | null => {
    const { event, theme } = props;

    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));
    const color = getEventColors(theme);

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceMemoryLevelChangedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceMemoryLevelChangedDescription"
                values={{
                    actorTitle: actorLabel,
                    entityId: event.entityId,
                }}
            />
        ),
    };
};

export const renderDeviceEventContent = (event: DeviceEvents, context: EventContext, theme: Theme): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "DeviceEvent.Created":
            return renderDeviceCreated({ event, context, theme });
        case "DeviceEvent.Updated":
            return renderDeviceUpdated({ event, context, theme });
        case "DeviceEvent.Activated":
            return renderDeviceActivated({ event, context, theme });
        case "DeviceEvent.LocationChanged":
            return renderDeviceLocationChanged({ event, context, theme });
        case "DeviceEvent.BatteryAlertDetected":
            return renderDeviceBatteryAlertDetected({ event, context, theme });
        case "DeviceEvent.BatteryHealthDegraded":
            return renderDeviceBatteryHealthDegraded({ event, context, theme });
        case "DeviceEvent.MemoryAlertDetected":
            return renderDeviceMemoryAlertDetected({ event, context, theme });
        case "DeviceEvent.Renamed":
            return renderDeviceRenamed({ event, context, theme });
        case "DeviceEvent.Deleted":
            return renderDeviceDeleted({ event, context, theme });
        case "DeviceEvent.BatteryLevelChanged":
            return renderDeviceBatteryLevelChanged({ event, context, theme });
        case "DeviceEvent.MemoryLevelChanged":
            return renderDeviceMemoryLevelChanged({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
